import { useContext, useState } from "react";

import {
  ShipperQuoteRequest,
  useCreateQuoteRequestMutation,
  useUpdateShipperQuoteRequestMutation,
} from "api/rest/quote-requests";
import { useCreateTemplateFromQuoteRequestMutation } from "api/rest/quote-requests/createTemplateFromQuoteRequest";
import { CargoGroupContext } from "features/cargo-groups/CargoGroupProvider";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { useQuoteRequestSelector } from "../_store";
import { quoteRequestActions } from "../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../types";

const useCreateQrIfNotCreated = () => {
  const { quoteRequest: airQuoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const [createQuoteRequest] = useCreateQuoteRequestMutation();
  const [updateQuoteRequest] = useUpdateShipperQuoteRequestMutation();

  const createQrIfNotCreated = async () => {
    if (!airQuoteRequest.id) {
      const {
        data: {
          quoteRequest: { id },
        },
      } = await createQuoteRequest({ body: { mode: airQuoteRequest.mode ?? "FTL" } }).unwrap();

      await updateQuoteRequest({
        body: { ...airQuoteRequest } as unknown as ShipperQuoteRequest,
        urlParams: { quoteRequestId: id },
      }).unwrap();

      return id;
    }

    return airQuoteRequest.id;
  };

  return createQrIfNotCreated;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useFinalizeAndSendQr = () => {
  const { t } = useTranslation(["common", "shipper"]);
  const {
    quoteRequest: airQuoteRequest,
    stepIndex,
    templateName,
    createTemplate,
  } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const resetState = quoteRequestActions.useResetState();
  const [updateQuoteRequest] = useUpdateShipperQuoteRequestMutation();
  const [createTemplateMutation] = useCreateTemplateFromQuoteRequestMutation();
  const { enqueueSnackbar } = useSnackbar();
  const createQrIfNotCreated = useCreateQrIfNotCreated();
  const { save: saveCargoGroups } = useContext(CargoGroupContext);
  const [resetLocalState, setResetLocalState] = useState(false);

  const finalizeAndSendQr = async () => {
    const qrId = await createQrIfNotCreated();

    if (stepIndex === QuoteStepIndex.Review) {
      // We drop the trucks field because it is not actually used in the create flow and will cause issues
      // with the backend if we try to use it.
      const { load_spec, ...quoteRequest } = airQuoteRequest;
      const { trucks: _, ...remainingLoadSpec } = load_spec || {};
      await updateQuoteRequest({
        body: { ...quoteRequest, load_spec: { ...remainingLoadSpec } } as unknown as ShipperQuoteRequest,
        urlParams: { quoteRequestId: qrId },
      }).unwrap();

      if (airQuoteRequest.mode === "DRAYAGE" || airQuoteRequest.mode === "FTL") {
        saveCargoGroups();
      }

      // We need to separate the SENT change from the other changes because the BE has some validation that
      // requires the existing changes to have been made already
      await updateQuoteRequest({
        body: { status: "SENT" } as unknown as ShipperQuoteRequest,
        urlParams: { quoteRequestId: qrId },
      }).unwrap();

      if (createTemplate) {
        await createTemplateMutation({ urlParams: { quoteRequestId: qrId }, body: { templateName } }).unwrap();
      }

      enqueueSnackbar(t("shipper:requestQuoteFlowSuccess.title"), { variant: "success" });
      resetState();
      setResetLocalState(true);
      setTimeout(() => {
        setResetLocalState(false);
      }, 100);
    }
  };

  return { finalizeAndSendQr, resetLocalState };
};
